import { Globe } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  LANGUAGE_SWITCHER_TITLE,
  LOCALE_NATIVE_LABELS,
  MIVO_LOCALES,
  getTranslation,
  useIsMultiLanguageEnabled,
  useLocale,
  type MivoTranslationKey,
} from "@mivo/i18n";

export type LanguageChromeProps = {
  className?: string;
  /** Primary-bar shell tint (FR-13). */
  tone?: "default" | "onPrimary";
  onPersist?: (locale: MivoTranslationKey) => Promise<void>;
  "data-testid"?: string;
};

export function LanguageChrome({
  className,
  tone = "default",
  onPersist,
  "data-testid": dataTestId = "language-switcher",
}: LanguageChromeProps) {
  const enabled = useIsMultiLanguageEnabled();
  const { locale, setLocale } = useLocale({ onPersist });
  const title = getTranslation(LANGUAGE_SWITCHER_TITLE);

  if (!enabled) {
    return null;
  }

  return (
    <div className={cn("inline-flex h-8 items-center", className)} data-testid={dataTestId}>
      <DropdownMenu>
        <DropdownMenuTrigger
          data-testid={`${dataTestId}-trigger`}
          aria-label={title}
          className={cn(
            "inline-flex size-8 items-center justify-center rounded-lg border-0 bg-transparent shadow-none outline-none transition focus-visible:ring-3 focus-visible:ring-ring/50",
            tone === "default"
              && "text-muted-foreground hover:bg-muted hover:text-foreground data-popup-open:bg-muted data-popup-open:text-foreground",
            tone === "onPrimary"
              && "text-primary-foreground/90 hover:bg-white/15 hover:text-primary-foreground data-popup-open:bg-white/15 data-popup-open:text-primary-foreground",
          )}
        >
          <Globe className="size-4" aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          data-testid={`${dataTestId}-popover`}
          className="w-auto min-w-40"
        >
          <DropdownMenuGroup>
            <DropdownMenuLabel className="px-1.5 py-1 text-sm font-semibold text-foreground">
              {title}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={locale}
              onValueChange={(value) => {
                if (!value) {
                  return;
                }
                void setLocale(value as MivoTranslationKey);
              }}
              data-testid={`${dataTestId}-options`}
            >
              {MIVO_LOCALES.map((key) => (
                <DropdownMenuRadioItem
                  key={key}
                  value={key}
                  data-testid={`${dataTestId}-${key}`}
                >
                  {LOCALE_NATIVE_LABELS[key]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
